import React, { useCallback, useEffect, useState } from "react";

import isEqual from "lodash/isEqual";
import moment from "moment";

import { getDigitalAgronomistMainCrops } from "services";

import { useAddOrUpdateDigitalAgronomistPreferences, useCancelAsyncOnUnmount, useTranslation } from "utils";

import {
  CropRecommendationResponse,
  CropRecommendationResponsePreference,
  INVESTMENT_LEVEL,
  MainCropOption,
  NavigationSplitViewGroupType,
  NavigationSplitViewOnTileClickedProps,
  NavigationSplitViewTileType,
  PH_TYPE,
  SOIL_TYPE,
} from "models";

import { Form, NavigationSplitView, PageSpin } from "components";

import OnboardingStepFormItem from "pages/app/digital-agronomist/components/OnboardingStepFormItem";
import WelcomeScreen from "pages/app/digital-agronomist/components/WelcomeScreen";
import {
  FORM_ITEM_TYPE,
  MainCropDetails,
  OnboardingFormValues,
  convertStepValueToLabel,
  generatePreferenceRequestBody,
  getValueTranslationKey,
  joinStringValues,
  mainCropFieldNameRegexTester,
} from "pages/app/digital-agronomist/utils.tsx";

enum ONBOARDING_STEP_GROUP_NAME {
  FARM_PROFILE = "farmProfile",
  CROP_ROTATION_PREFERENCES = "cropRotationPreferences",
  CROP_ROTATION_MAIN_CROPS = "cropRotationMainCrops",
}

export type OnboardingStep = {
  name: keyof OnboardingFormValues;
  type: FORM_ITEM_TYPE;
  options: {
    translationKey: string;
    value: string | number | boolean;
    prefix?: string;
    suffix?: string;
  }[];
  group: ONBOARDING_STEP_GROUP_NAME;
  show?: boolean;
  year?: string;
};

const onboardingSteps: OnboardingStep[] = [
  {
    name: "isDrought",
    type: FORM_ITEM_TYPE.RADIO,
    options: [
      { translationKey: "yes", value: true },
      { translationKey: "no", value: false },
    ],
    group: ONBOARDING_STEP_GROUP_NAME.FARM_PROFILE,
    show: true,
  },
  {
    name: "pHType",
    type: FORM_ITEM_TYPE.RADIO,
    options: [
      { translationKey: "acidic", value: PH_TYPE.ACIDIC },
      { translationKey: "neutral", value: PH_TYPE.NEUTRAL },
      { translationKey: "alkaline", value: PH_TYPE.ALKALINE },
    ],
    group: ONBOARDING_STEP_GROUP_NAME.FARM_PROFILE,
  },
  {
    name: "soilType",
    type: FORM_ITEM_TYPE.RADIO,
    options: [
      { translationKey: "heavy", value: SOIL_TYPE.HEAVY },
      { translationKey: "medium", value: SOIL_TYPE.MEDIUM },
      { translationKey: "light", value: SOIL_TYPE.LIGHT },
    ],
    group: ONBOARDING_STEP_GROUP_NAME.FARM_PROFILE,
  },
  {
    name: "legumeBreaksYears",
    type: FORM_ITEM_TYPE.SELECT,
    options: [
      { translationKey: "notApplicable", value: 0 },
      { translationKey: "year", value: 1, prefix: "≤ 1" },
      { translationKey: "years", value: 2, prefix: "2" },
      { translationKey: "years", value: 3, prefix: "3" },
      { translationKey: "years", value: 4, prefix: "4" },
      { translationKey: "years", value: 5, prefix: "≥ 5" },
    ],
    group: ONBOARDING_STEP_GROUP_NAME.CROP_ROTATION_PREFERENCES,
  },
  {
    name: "investmentLevel",
    type: FORM_ITEM_TYPE.RADIO,
    options: [
      {
        translationKey: "lowAmount",
        value: INVESTMENT_LEVEL.LOW,
      },
      {
        translationKey: "mediumAmount",
        value: INVESTMENT_LEVEL.MEDIUM,
      },
      {
        translationKey: "highAmount",
        value: INVESTMENT_LEVEL.HIGH,
      },
    ],
    group: ONBOARDING_STEP_GROUP_NAME.CROP_ROTATION_PREFERENCES,
  },
  {
    name: "isFodder",
    type: FORM_ITEM_TYPE.RADIO,
    options: [
      { translationKey: "yes", value: true },
      { translationKey: "no", value: false },
    ],
    group: ONBOARDING_STEP_GROUP_NAME.CROP_ROTATION_PREFERENCES,
  },
  {
    name: "isBiogas",
    type: FORM_ITEM_TYPE.RADIO,
    options: [
      { translationKey: "yes", value: true },
      { translationKey: "no", value: false },
    ],
    group: ONBOARDING_STEP_GROUP_NAME.CROP_ROTATION_PREFERENCES,
  },
  {
    name: "mainCropsNumber",
    type: FORM_ITEM_TYPE.SELECT,
    options: [
      { translationKey: "crops", value: 2, prefix: "2" },
      { translationKey: "crops", value: 3, prefix: "3" },
      { translationKey: "crops", value: 4, prefix: "4" },
      { translationKey: "crops", value: 5, prefix: "5" },
    ],
    group: ONBOARDING_STEP_GROUP_NAME.CROP_ROTATION_MAIN_CROPS,
  },
];

// we need to add 1 to this number to account for the missing "1 crop" option
const mainCropsNumber = onboardingSteps.find((step) => step.name === "mainCropsNumber")!.options.length + 1;
const availableMainCropYears: string[] = new Array(mainCropsNumber).fill(new Date().getFullYear()).map((year, i) => (year + i).toString());

const mainCropSteps: OnboardingStep[] = availableMainCropYears.map((year, i) => ({
  name: `mainCrop${i + 1}` as keyof OnboardingFormValues,
  type: FORM_ITEM_TYPE.MAIN_CROP,
  options: [],
  group: ONBOARDING_STEP_GROUP_NAME.CROP_ROTATION_MAIN_CROPS,
  year,
}));

const getOnboardingFormStepNames = (): Array<keyof OnboardingFormValues> => {
  const preferencesStepNames = onboardingSteps.map((step) => step.name);
  const mainCropsStepNames = mainCropSteps.map((step) => step.name);
  return [...preferencesStepNames, ...mainCropsStepNames];
};

export default function Onboarding({ data }: { data: CropRecommendationResponse }) {
  const { t } = useTranslation("digitalAgronomist");
  const signal = useCancelAsyncOnUnmount()();

  const [mainCropsList, setMainCropsList] = useState<MainCropOption[]>([]);

  const [fieldNameToUpdate, setFieldNameToUpdate] = useState<keyof OnboardingFormValues | null>();
  const [showOnboardingSteps, setShowOnboardingSteps] = useState<boolean>(false);
  const [activeTileIndex, setActiveTileIndex] = useState<NavigationSplitViewOnTileClickedProps>({
    groupIndex: 0,
    subGroupIndex: 0,
    tileIndex: 0,
  });
  const [mainCropsNumber, setMainCropsNumber] = useState<number | null>(null);
  const [onboardingStepsErrors, setOnboardingStepsErrors] = useState<NavigationSplitViewOnTileClickedProps[]>([]);
  const [activeMainCropInitialFormValue, setActiveMainCropInitialFormValue] = useState<MainCropDetails | null>(null);
  const [waitingForRecommendations, setWaitingForRecommendations] = useState<boolean>(false);

  const { mutate: addOrUpdateDigitalAgronomistPreferences } = useAddOrUpdateDigitalAgronomistPreferences();
  const [form] = Form.useForm();

  // eslint-disable-next-line
  const handleNextStep = (fieldName: keyof OnboardingFormValues, showNextTile: boolean = true) => {
    const currentTileIndex = onboardingSteps.findIndex((step) => step.name === fieldName);
    const groupTileIndex = getTileIndexByName(fieldName);
    if (!groupTileIndex) return;

    const { groupIndex, subGroupIndex, tileIndex } = groupTileIndex;
    const updatedGroupsTiles = [...tilesGroups];
    let nextTileName: keyof OnboardingFormValues | undefined = mainCropFieldNameRegexTester.test(fieldName)
      ? (updatedGroupsTiles[groupIndex].subGroups[subGroupIndex].tiles[tileIndex + 1]?.name as keyof OnboardingFormValues) || undefined
      : onboardingSteps[currentTileIndex + 1]?.name || undefined;

    form
      .validateFields([fieldName], { recursive: true })
      .then(async () => {
        const currentFormValues = form.getFieldsValue(true);

        if (fieldName === "mainCropsNumber") {
          const numberOfMainCrops = form.getFieldValue(fieldName);
          setMainCropsNumber(numberOfMainCrops);
          const yearsToInclude = availableMainCropYears.slice(0, numberOfMainCrops);
          const existingMainCropTiles = tilesGroups[groupIndex].subGroups[subGroupIndex].tiles.filter((tile) => mainCropFieldNameRegexTester.test(tile.name));
          const updatedCropsList = mainCropsList?.length > 0 ? mainCropsList : await getDigitalAgronomistMainCrops({ signal });

          const newMainCropsTiles: NavigationSplitViewTileType[] = mainCropSteps.map((step) =>
            generateTile(step, t, handleNextStep, numberOfMainCrops, updatedCropsList)
          );

          const updatedMainCropTiles = yearsToInclude.map((year, i) => {
            if (existingMainCropTiles[i]) {
              const { show, value } = existingMainCropTiles[i];
              return {
                ...newMainCropsTiles[i],
                show,
                value,
              };
            }
            return newMainCropsTiles[i];
          });

          // we need to make sure to remove from the form all the main crops that are exceeding the updated numberOfMainCrops
          existingMainCropTiles.forEach((tile, i) => {
            if (i + 1 > numberOfMainCrops) {
              delete currentFormValues[tile.name];
            }
          });
          form.setFieldsValue({ ...currentFormValues });

          updatedGroupsTiles[groupIndex].subGroups[subGroupIndex].tiles = [
            { ...updatedGroupsTiles[groupIndex].subGroups[subGroupIndex].tiles[tileIndex] },
            ...updatedMainCropTiles,
          ];
          nextTileName = updatedMainCropTiles[0].name as keyof OnboardingFormValues;

          if (!showNextTile) {
            setTilesGroups(updatedGroupsTiles);
            return;
          }
        }

        if (nextTileName) {
          const updatedTiles = updateTile(updatedGroupsTiles, nextTileName, undefined, true);
          setTilesGroups(updatedTiles);
          const updatedActiveIndex = getTileIndexByName(nextTileName);
          if (updatedActiveIndex) setActiveTileIndex(updatedActiveIndex);
        }

        const onboardingStepsWithErrors: NavigationSplitViewOnTileClickedProps[] = [];

        // we need to make sure that sowing date is always before harvest date
        const mainCropFormSteps = Object.entries(currentFormValues).filter((entry) => mainCropFieldNameRegexTester.test(entry[0]));
        const fieldsWithErrors = mainCropFormSteps
          .map(([fieldName, fieldValue]) =>
            (fieldValue as MainCropDetails).harvestDate.isBefore((fieldValue as MainCropDetails).sowingDate, "day")
              ? (fieldName as keyof OnboardingFormValues)
              : null
          )
          .filter((fieldName) => fieldName !== null);

        const isCorrectNumberOfMainCrops = mainCropFormSteps.length === currentFormValues.mainCropsNumber;
        if (!isCorrectNumberOfMainCrops) {
          const mainCropsNumberTileIndex = getTileIndexByName("mainCropsNumber");
          if (mainCropsNumberTileIndex !== null) onboardingStepsWithErrors.push(mainCropsNumberTileIndex);
        } else {
          setWaitingForRecommendations(true);
        }

        if (fieldsWithErrors.length > 0) {
          const mainCropsTileIndexesWithError = fieldsWithErrors
            .map((fieldName) => getTileIndexByName(fieldName as keyof OnboardingFormValues))
            .filter((tileIndex) => tileIndex !== null) as NavigationSplitViewOnTileClickedProps[];

          onboardingStepsWithErrors.push(...mainCropsTileIndexesWithError);
        }

        if (onboardingStepsWithErrors.length > 0) {
          setOnboardingStepsErrors(onboardingStepsWithErrors);
        }

        const preferenceRequestBody = generatePreferenceRequestBody(currentFormValues);
        addOrUpdateDigitalAgronomistPreferences({ requestBody: preferenceRequestBody, signal });
      })
      .catch((e: Error) => {
        console.error(e); // eslint-disable-line
      });
  };

  const [tilesGroups, setTilesGroups] = useState<NavigationSplitViewGroupType[]>(generateTilesGroups(t, handleNextStep, mainCropsNumber));

  const getTileIndexByName = useCallback(
    (fieldName: keyof OnboardingFormValues): NavigationSplitViewOnTileClickedProps | null => {
      const groupIndex = tilesGroups.findIndex((group) => group.subGroups.some((subGroup) => subGroup.tiles.some((tile) => tile.name === fieldName)));
      if (groupIndex === -1) return null;

      const subGroupIndex = tilesGroups[groupIndex].subGroups.findIndex((subGroup) => subGroup.tiles.some((tile) => tile.name === fieldName));
      if (subGroupIndex === -1) return null;

      const tileIndex = tilesGroups[groupIndex].subGroups[subGroupIndex].tiles.findIndex((tile) => tile.name === fieldName);
      if (tileIndex === -1) return null;

      return { groupIndex, subGroupIndex, tileIndex };
    },
    [tilesGroups]
  );

  const getTileNameByIndex = useCallback(
    (tileIndex: NavigationSplitViewOnTileClickedProps): keyof OnboardingFormValues | null => {
      if (!tileIndex) return null;

      const tileName = tilesGroups[tileIndex.groupIndex].subGroups[tileIndex.subGroupIndex].tiles[tileIndex.tileIndex].name;
      if (!tileName) return null;
      return tileName as keyof OnboardingFormValues;
    },
    [tilesGroups]
  );

  const updateTile = useCallback(
    (
      tilesGroups: NavigationSplitViewGroupType[],
      fieldName: keyof OnboardingFormValues | null,
      value?: OnboardingFormValues[keyof OnboardingFormValues],
      showTile?: boolean
    ) => {
      if (!fieldName) return tilesGroups;

      const groupTileIndex = getTileIndexByName(fieldName);
      if (!groupTileIndex) return tilesGroups;

      const { groupIndex, subGroupIndex, tileIndex } = groupTileIndex;
      const tileToUpdate = tilesGroups[groupIndex].subGroups[subGroupIndex].tiles[tileIndex];
      const translationKey = getValueTranslationKey(fieldName, value, onboardingSteps);

      const updatedTile = {
        ...tileToUpdate,
        value: value !== undefined ? convertStepValueToLabel(t, fieldName, value, translationKey) : tileToUpdate.value,
        show: typeof showTile === "boolean" ? showTile : tileToUpdate.show,
      };

      const updatedTiles = [...tilesGroups];
      updatedTiles[groupIndex].subGroups[subGroupIndex].tiles[tileIndex] = updatedTile;
      return updatedTiles;
    },
    [getTileIndexByName, t]
  );

  const fetchMainCropsList = useCallback(async () => {
    const cropsList = await getDigitalAgronomistMainCrops({ signal });
    setMainCropsList(cropsList);
  }, [signal]);

  useEffect(() => {
    fetchMainCropsList();
  }, [fetchMainCropsList]);

  useEffect(() => {
    if (!fieldNameToUpdate) return;

    const formValues = form.getFieldsValue(true);
    const updatedValue = mainCropFieldNameRegexTester.test(fieldNameToUpdate)
      ? mainCropsList?.find((crop) => crop.id === formValues[fieldNameToUpdate].cropId)?.name
      : formValues[fieldNameToUpdate];
    const updatedTiles = updateTile(tilesGroups, fieldNameToUpdate, updatedValue);
    setTilesGroups(updatedTiles);
    setFieldNameToUpdate(null);
  }, [mainCropsList, fieldNameToUpdate, form, tilesGroups, updateTile]);

  const resetActiveMainCropValue = useCallback(() => {
    if (activeMainCropInitialFormValue !== null) return;

    const currentStepName = getTileNameByIndex(activeTileIndex);
    if (!currentStepName) return;

    const newInitialValue = form.getFieldValue([currentStepName as keyof OnboardingFormValues]);
    if (newInitialValue === undefined) return;

    setActiveMainCropInitialFormValue(newInitialValue);
  }, [activeMainCropInitialFormValue, activeTileIndex, form, getTileNameByIndex]);

  const getCurrentStepName = useCallback<() => keyof OnboardingFormValues>(() => {
    const stepNames = getOnboardingFormStepNames();
    if (data?.cropRecommendations.length) {
      const mainCropsIndex = stepNames.indexOf("mainCropsNumber");
      return stepNames[mainCropsIndex + data.cropRecommendations.length + 1];
    }

    const currentIndex = stepNames.findLastIndex((key) => data?.preferences[key as keyof CropRecommendationResponsePreference] != null);
    return stepNames[currentIndex + 1];
  }, [data?.cropRecommendations?.length, data?.preferences]);

  const updateTilesFromPreferences = useCallback(
    (updatedGroupsTiles: NavigationSplitViewGroupType[]) => {
      let updatedTiles: NavigationSplitViewGroupType[] = [];
      Object.entries(data?.preferences || {}).forEach(([key, value]) => {
        updatedTiles = updateTile(
          updatedGroupsTiles,
          key as keyof OnboardingFormValues,
          value as OnboardingFormValues[keyof OnboardingFormValues],
          value !== null
        );
        form.setFieldValue(key, value);
      });
      return updatedTiles;
    },
    [data, form, updateTile]
  );

  const updateMainCropTiles = useCallback(
    (yearsToInclude: string[], updatedCropsList: MainCropOption[], newMainCropsTiles: NavigationSplitViewTileType[]) =>
      yearsToInclude.map((year, i) => {
        const mainCropTargetYear = data?.cropRecommendations?.find((mainCrop) => mainCrop.mainCrop.year === Number(year));
        if (mainCropTargetYear) {
          form.setFieldValue(`mainCrop${i + 1}`, {
            cropId: mainCropTargetYear.mainCrop.mainCropId,
            sowingDate: moment(mainCropTargetYear.mainCrop.seedingDate),
            harvestDate: moment(mainCropTargetYear.mainCrop.harvestDate),
            year: mainCropTargetYear.mainCrop.year,
          });

          const cropName = updatedCropsList.find((crop) => crop.id === mainCropTargetYear.mainCrop.mainCropId)?.name;
          return { ...newMainCropsTiles[i], show: true, value: cropName };
        }
        return newMainCropsTiles[i];
      }),
    [data?.cropRecommendations, form]
  );

  const updateGroupTiles = useCallback(
    (updatedGroupsTiles: NavigationSplitViewGroupType[], updatedMainCropTiles: NavigationSplitViewTileType[]) => {
      const groupTileIndex = getTileIndexByName("mainCropsNumber");
      if (!groupTileIndex) return;
      const { groupIndex, subGroupIndex } = groupTileIndex;
      updatedGroupsTiles[groupIndex].subGroups[subGroupIndex].tiles.push(...updatedMainCropTiles);
      setTilesGroups(updatedGroupsTiles);
    },
    [getTileIndexByName]
  );

  const setActiveTile = useCallback(
    (nextTileName: keyof OnboardingFormValues, updatedGroupsTiles: NavigationSplitViewGroupType[]) => {
      const updatedTiles = updateTile(updatedGroupsTiles, nextTileName, undefined, true);
      setTilesGroups(updatedTiles);
      const updatedActiveIndex = getTileIndexByName(nextTileName);
      if (updatedActiveIndex) setActiveTileIndex(updatedActiveIndex);
    },
    [getTileIndexByName, updateTile]
  );

  const handleMainCropsNumber = useCallback(
    async (updatedGroupsTiles: NavigationSplitViewGroupType[], nextTileName: keyof OnboardingFormValues | null) => {
      setMainCropsNumber(data?.preferences?.mainCropsNumber);
      const yearsToInclude = availableMainCropYears.slice(0, data?.preferences?.mainCropsNumber ?? 0);
      const updatedCropsList: MainCropOption[] = mainCropsList?.length > 0 ? mainCropsList : await getDigitalAgronomistMainCrops({ signal });

      const newMainCropsTiles = mainCropSteps.map((step) => generateTile(step, t, handleNextStep, data?.preferences?.mainCropsNumber, updatedCropsList));

      const updatedMainCropTiles = updateMainCropTiles(yearsToInclude, updatedCropsList, newMainCropsTiles);

      updateGroupTiles(updatedGroupsTiles, updatedMainCropTiles);
      if (nextTileName) setActiveTile(nextTileName, updatedGroupsTiles);
    },
    [data?.preferences?.mainCropsNumber, handleNextStep, mainCropsList, setActiveTile, signal, t, updateGroupTiles, updateMainCropTiles]
  );

  const setCurrentlyInProgressTile = useCallback(async () => {
    const currentStepName = getCurrentStepName();
    const updatedGroupsTiles = [...tilesGroups];
    let updatedTiles = updateTilesFromPreferences(updatedGroupsTiles);

    if (!data?.preferences?.mainCropsNumber) {
      updatedTiles = updateTile(tilesGroups, currentStepName, undefined, true);
      setTilesGroups(updatedTiles);
      const updatedActiveIndex = getTileIndexByName(currentStepName);
      if (updatedActiveIndex) setActiveTileIndex(updatedActiveIndex);
    } else {
      await handleMainCropsNumber(updatedGroupsTiles, currentStepName);
    }
  }, [getCurrentStepName, tilesGroups, updateTilesFromPreferences, data?.preferences?.mainCropsNumber, updateTile, getTileIndexByName, handleMainCropsNumber]);

  useEffect(() => {
    if (showOnboardingSteps) return;
    const formSteps = getOnboardingFormStepNames();
    const isFirstStep =
      !data?.preferences ||
      (formSteps.slice(0, formSteps.indexOf("mainCropsNumber")).every((step) => (data?.preferences as OnboardingFormValues)[step] === null) &&
        !data?.cropRecommendations.length);

    if (isFirstStep) {
      resetActiveMainCropValue();
    } else {
      setShowOnboardingSteps(true);
      setCurrentlyInProgressTile();
    }
  }, [data, resetActiveMainCropValue, setCurrentlyInProgressTile, showOnboardingSteps]);

  return waitingForRecommendations ? (
    <PageSpin />
  ) : (
    <Form
      className="onboarding-form"
      name="digital-agronomist-onboarding-form"
      form={form}
      onValuesChange={(changedValues: OnboardingFormValues) => {
        const fieldName = Object.keys(changedValues)[0] as keyof OnboardingFormValues;
        setFieldNameToUpdate(fieldName);

        const fieldValue = form.getFieldValue(fieldName);
        const dirtyCondition =
          mainCropFieldNameRegexTester.test(fieldName) && Object.values(fieldValue).length === 3 && Object.values(fieldValue).every((value) => value);

        form
          .validateFields([fieldName], { recursive: true, dirty: !dirtyCondition })
          .then(() => {
            const updatedErrorsList = onboardingStepsErrors.filter((error) => !isEqual(error, activeTileIndex));
            setOnboardingStepsErrors(updatedErrorsList);
          })
          .catch((errorsList) => {
            if (errorsList.errorFields.length === 0) {
              const updatedErrorsList = onboardingStepsErrors.filter((error) => !isEqual(error, activeTileIndex));
              setOnboardingStepsErrors(updatedErrorsList);
              return;
            }
            if (onboardingStepsErrors.some((error) => isEqual(error, activeTileIndex))) return;
            setOnboardingStepsErrors([...onboardingStepsErrors, activeTileIndex]);
          });
      }}
    >
      {showOnboardingSteps ? (
        <NavigationSplitView
          className="padding-200"
          title={t("onboarding.title")}
          groups={tilesGroups}
          activeTileIndex={activeTileIndex}
          setActiveTileIndex={setActiveTileIndex}
          onTileClick={() => {
            if (onboardingStepsErrors.some((error) => isEqual(error, activeTileIndex))) {
              const activeFieldName = getTileNameByIndex(activeTileIndex);
              form.setFieldValue(activeFieldName, activeMainCropInitialFormValue);
            }
          }}
          onTileChanged={() => setActiveMainCropInitialFormValue(null)}
        />
      ) : (
        <WelcomeScreen onFormStart={() => setShowOnboardingSteps(true)} />
      )}
    </Form>
  );
}

const generateTile = (
  step: OnboardingStep,
  t: (key: string) => string,
  handleNextStep: (fieldName: keyof OnboardingFormValues) => void,
  mainCropsNumber?: number | null,
  mainCropsList?: MainCropOption[]
) => {
  const isMainCropStep = step.type === FORM_ITEM_TYPE.MAIN_CROP;
  const translationFieldName = isMainCropStep ? "mainCropDetails" : step.name;
  const title = t(`onboarding.steps.${translationFieldName}.shortTitle`);
  const ctaButtonLabel =
    mainCropsNumber && isMainCropStep && step.name.endsWith(mainCropsNumber.toString()) ? t("onboarding.generateRecommendations") : t("onboarding.continue");
  const options =
    isMainCropStep && mainCropsList
      ? mainCropsList.map((crop) => ({ label: crop.name, value: crop.id })).sort((a, b) => a.label.localeCompare(b.label))
      : step.options.map((option) => ({
          label: joinStringValues([option.prefix || "", t(`onboarding.options.${option.translationKey}`), option.suffix || ""]),
          value: option.value,
        }));
  const subtitle =
    t(`onboarding.steps.${translationFieldName}.subtitle`) === "{{subtitlePlaceholder}}" ? undefined : t(`onboarding.steps.${translationFieldName}.subtitle`);

  const tile = {
    title: isMainCropStep ? `${title} ${step.year}` : title,
    name: step.name,
    value: undefined,
    show: step.show || false,
    groupKey: step.group,
    main: (
      <OnboardingStepFormItem
        fieldName={step.name}
        title={t(`onboarding.steps.${translationFieldName}.title`)}
        type={step.type}
        t={t}
        ctaButtonLabel={ctaButtonLabel}
        options={options}
        inputSelectLabel={t(`onboarding.steps.${translationFieldName}.shortTitle`)}
        handleNextStep={handleNextStep}
        year={step.year}
        subtitle={subtitle}
        progressPercentage={getOnboardingProgress(step.name, mainCropsNumber)}
        mainCropsList={mainCropsList}
      />
    ),
  };

  return tile;
};

const generateTilesGroups = (
  t: (key: string) => string,
  handleNextStep: (fieldName: keyof OnboardingFormValues) => void,
  mainCropsNumber?: number | null
): NavigationSplitViewGroupType[] => {
  const tiles = onboardingSteps.map((step) => generateTile(step, t, handleNextStep, mainCropsNumber));

  const groups = [
    {
      title: t("onboarding.farmProfile"),
      subGroups: [{ tiles: tiles.filter((tile) => tile.groupKey === ONBOARDING_STEP_GROUP_NAME.FARM_PROFILE) }],
    },
    {
      title: t("onboarding.cropRotationPreferences"),
      subGroups: [
        {
          subtitle: t("onboarding.preferences"),
          tiles: tiles.filter((tile) => tile.groupKey === ONBOARDING_STEP_GROUP_NAME.CROP_ROTATION_PREFERENCES),
        },
        {
          subtitle: t("onboarding.mainCrops"),
          tiles: tiles.filter((tile) => tile.groupKey === ONBOARDING_STEP_GROUP_NAME.CROP_ROTATION_MAIN_CROPS),
        },
      ],
    },
  ];
  return groups;
};

const getOnboardingProgress = (fieldName: keyof OnboardingFormValues, numberOfMainCrops?: number | null) => {
  const totalMainSteps = onboardingSteps.length + 1;
  const isMainCropStep = mainCropFieldNameRegexTester.test(fieldName);

  if (isMainCropStep) {
    const stepIndex = parseInt(fieldName.replace("mainCrop", ""));
    return ((totalMainSteps - 1 + stepIndex / (numberOfMainCrops || 1)) / totalMainSteps) * 100;
  }

  const stepIndex = onboardingSteps.findIndex((step) => step.name === fieldName) + 1;
  return (stepIndex / totalMainSteps) * 100;
};
